import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../authProvider.mjs';
import PageShell from '../../components/PageShell.mjs';
import { seedFiscalYear } from '../../utils/fiscalYear.mjs';
import RejectionsTab from '../components/RejectionsTab.mjs';
import ClaimsByPersonTab from '../components/ClaimsByPersonTab.mjs';
import FundingTab from '../components/FundingTab.mjs';
import BreakdownTab from '../components/BreakdownTab.mjs';
import DownloadsTab from '../components/DownloadsTab.mjs';

/**
 * The Reports module: read-only reporting and records. Approving stays on
 * each role's own dashboard.
 *
 * Only finished work is reported here - claims that have reached their final
 * approval. Drafts and anything still in the approval pipeline are left out
 * on purpose; the dashboards are where work in progress is followed.
 *
 * The tab, fiscal year, quarter and region are all held in the URL, so a view
 * can be linked to, survives a tab switch, and Back from a report opened here
 * returns to the view it was opened from.
 */
const TAB = {
  rejections: { key: 'rejections', label: 'Rejections', Component: RejectionsTab },
  people: { key: 'people', label: 'Claims by person', Component: ClaimsByPersonTab },
  funding: { key: 'funding', label: 'Funding by PTAE', Component: FundingTab },
  breakdown: { key: 'breakdown', label: 'Breakdown', Component: BreakdownTab },
  downloads: { key: 'downloads', label: 'Downloads', Component: DownloadsTab },
};

// The auditor's decisions are the auditor's to review, so only they (and a
// delegate covering for them) get Rejections; the server refuses everyone
// else, this only keeps the tab from being offered.
const AUDITOR_TABS = [TAB.rejections, TAB.people, TAB.funding, TAB.downloads];
const DIRECTOR_TABS = [TAB.breakdown, TAB.downloads];

// Roles whose remit is every region, and so get a region filter. Regional
// directors and admins see their own region only, and the server enforces
// that whatever region is asked for.
const GLOBAL_ROLES = ['auditor', 'global_director', 'finance_director'];

const QUARTER_KEYS = ['1', '2', '3', '4'];

const ReportsPage = () => {
  const { user } = useContext(AuthContext);
  const [params, setParams] = useSearchParams();

  // By role only. Covering for the auditor opens the auditor dashboard, not
  // the auditor's Reports: the server scopes Reports by role too
  // (finalClaims.reportsScope), so an admin covering stays on their region.
  const auditorView = user?.role === 'auditor';
  const isGlobal = auditorView || GLOBAL_ROLES.includes(user?.role);
  const tabs = auditorView ? AUDITOR_TABS : DIRECTOR_TABS;
  // An unknown or not-for-this-role ?tab= falls back to the role's first tab.
  const active = tabs.find(t => t.key === params.get('tab')) || tabs[0];

  const fyParam = parseInt(params.get('fy'), 10);
  const fy = Number.isFinite(fyParam) && fyParam > 2000 ? fyParam : seedFiscalYear();
  const quarter = QUARTER_KEYS.includes(params.get('q')) ? Number(params.get('q')) : 'all';
  // A region in the URL is honoured only for the global roles; anyone else
  // always asks for 'all' and the server answers with their own region.
  const region = isGlobal ? (params.get('region') || 'all') : 'all';

  // Regions learned from what the tabs load (the claims read returns every
  // region in scope), so the picker does not shrink to the one selected.
  const [known, setKnown] = useState([]);
  const addRegions = useCallback((list) => {
    const clean = (list || []).filter(r => r && r !== 'Unknown');
    if (clean.length === 0) return;
    setKnown(prev => {
      const next = [...new Set([...prev, ...clean])].sort();
      return next.length === prev.length ? prev : next;
    });
  }, []);

  const update = useCallback((changes) => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => {
        if (v === null || v === undefined || v === 'all' || v === '') next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }, [setParams]);

  const scope = useMemo(() => ({
    fy, quarter, region, isGlobal, auditorView,
    userRegion: user?.region || null,
    regions: isGlobal ? known : [],
    addRegions,
    setFy: (v) => update({ fy: v }),
    setQuarter: (v) => update({ q: v }),
    setRegion: (v) => update({ region: v }),
    // Where Back from a report or claim opened here should land.
    here: `/reports?${params.toString()}`,
  }), [fy, quarter, region, isGlobal, auditorView, user?.region, known, addRegions, update, params]);

  const subtitle = auditorView
    ? 'Audit decisions, finally approved claims, PTAE funding and downloads, for every region'
    : isGlobal
      ? 'Finally approved spend and claim downloads, for every region'
      : `Finally approved spend and claim downloads for ${user?.region || 'your region'}`;

  return (
    <PageShell title="Reports" subtitle={subtitle}>
      {tabs.length > 1 && (
        <div role="tablist" aria-label="Reports" className="flex flex-wrap gap-2 mb-4 sm:mb-6">
          {tabs.map(t => (
            <button
              key={t.key}
              role="tab"
              aria-selected={t.key === active.key}
              onClick={() => update({ tab: t.key })}
              className={`px-4 py-2 text-sm rounded-full border transition-colors ${t.key === active.key
                ? 'bg-stanford-red text-white border-stanford-red'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      <active.Component scope={scope} />
    </PageShell>
  );
};

export default ReportsPage;
